import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Clock, Rocket } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Public social-proof strip shown under the "Get pricing in 60 seconds"
 * heading. Three prominent stat tiles with big numbers + label below.
 * Self-fetching; renders nothing if API fails. */
export default function QuoteStatsChip() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/quote/stats`)
      .then((r) => { if (mounted) setStats(r.data); })
      .catch(() => { if (mounted) setErr(true); });
    return () => { mounted = false; };
  }, []);

  if (err || !stats) return null;

  const monthNum = stats.quotes_this_month ?? 0;
  const monthBig = monthNum > 0 ? String(monthNum) : "0";
  const monthLabel = monthNum > 0 ? "quotes generated this month" : "Be the first this month";

  const avgH = stats.avg_response_hours;
  const avgBig = avgH != null ? `~${avgH}` : "~6";
  const avgLabel = "hour avg response";

  const shippedNum = stats.projects_shipped ?? 0;
  const shippedBig = shippedNum > 0 ? String(shippedNum) : "—";
  const shippedLabel = shippedNum > 0
    ? (shippedNum === 1 ? "project shipped" : "projects shipped")
    : "founding team · white-glove";

  return (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto" data-testid="quote-stats-chip">
      <StatTile
        Icon={TrendingUp}
        big={monthBig}
        label={monthLabel}
        accent="cyan"
        testid="stats-this-month"
      />
      <StatTile
        Icon={Clock}
        big={avgBig}
        suffix="hr"
        label={avgLabel}
        accent="emerald"
        testid="stats-avg-response"
      />
      <StatTile
        Icon={Rocket}
        big={shippedBig}
        label={shippedLabel}
        accent="violet"
        testid="stats-shipped"
      />
    </div>
  );
}

const ACCENTS = {
  cyan:    { ring: "border-cyan-400/30",    glow: "rgba(34,211,238,0.10)",  icon: "text-cyan-300",    num: "text-cyan-200"    },
  emerald: { ring: "border-emerald-400/30", glow: "rgba(52,211,153,0.10)",  icon: "text-emerald-300", num: "text-emerald-200" },
  violet:  { ring: "border-violet-400/30",  glow: "rgba(167,139,250,0.10)", icon: "text-violet-300",  num: "text-violet-200"  },
};

function StatTile({ Icon, big, suffix, label, accent, testid }) {
  const A = ACCENTS[accent] || ACCENTS.cyan;
  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border ${A.ring} px-5 py-5 sm:py-6 backdrop-blur-sm transition-all hover:scale-[1.02] hover:border-opacity-60`}
      style={{ background: `linear-gradient(135deg, ${A.glow}, rgba(24,24,27,0.5) 60%)` }}
    >
      {/* Faint corner accent dot */}
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-40`} style={{ background: A.glow }} />
      <div className="relative flex items-start gap-3">
        <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-zinc-900/60 border border-zinc-800`}>
          <Icon size={18} className={A.icon} />
        </div>
        <div className="min-w-0">
          <p className={`text-3xl sm:text-4xl font-bold ${A.num} leading-none tracking-tight`}>
            {big}{suffix && <span className="text-xl sm:text-2xl ml-0.5 font-semibold opacity-80">{suffix}</span>}
          </p>
          <p className="text-zinc-400 text-sm mt-2 leading-snug">{label}</p>
        </div>
      </div>
    </div>
  );
}
